import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Send, CheckCircle2, XCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";

// Settings-page card for how POs reach suppliers (email + WhatsApp). Edits
// the same /auto-procurement/settings document as the Auto Procurement
// (MRP) → Settings tab; only the fields below are sent (partial update).
const FIELDS = ["email_enabled", "whatsapp_enabled", "whatsapp_provider", "whatsapp_phone_number_id",
  "whatsapp_template_name", "whatsapp_template_lang", "po_cc"];

function Status({ ok, okText, badText }) {
  const Icon = ok ? CheckCircle2 : XCircle;
  return (
    <span className={`inline-flex items-center gap-1 text-xs ${ok ? "text-emerald-700" : "text-red-600"}`}>
      <Icon className="w-3.5 h-3.5" /> {ok ? okText : badText}
    </span>
  );
}

// Test email + the last few emails with the provider's real delivery state,
// for when the app says "sent" but the supplier never receives anything.
function EmailDiagnostics() {
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [rows, setRows] = useState([]);
  const [delivery, setDelivery] = useState({});
  const load = () => api.get("/outbound-emails?limit=10").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const test = async () => {
    setBusy(true); setResult(null);
    try { const { data } = await api.post("/outbound-emails/test", { to }); setResult(data); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Test failed"); }
    finally { setBusy(false); }
  };
  const check = async (id) => {
    try { const { data } = await api.get(`/outbound-emails/${id}/delivery`); setDelivery((d) => ({ ...d, [id]: data })); }
    catch (e) { setDelivery((d) => ({ ...d, [id]: { delivery: "error", detail: formatApiError(e.response?.data?.detail) } })); }
  };
  const tone = (st) => (st === "sent" ? "text-emerald-700" : st === "queued" ? "text-amber-700" : "text-red-600");
  return (
    <div className="border-t border-slate-200 mt-5 pt-4 space-y-3" data-testid="email-diagnostics">
      <div className="text-sm font-semibold text-slate-800">Email check</div>
      <div className="flex gap-2">
        <Input type="email" placeholder="your.address@canares.com" value={to} onChange={(e) => setTo(e.target.value)} data-testid="email-test-to" />
        <Button variant="outline" disabled={busy || !to} onClick={test} data-testid="email-test-send">{busy ? "Sending…" : "Send test email"}</Button>
      </div>
      {result && (
        <div className={`text-xs ${tone(result.status)}`} data-testid="email-test-result">
          {result.status === "sent" ? "Accepted" : result.status === "queued" ? "Queued — NOT sent" : "Failed"}
          {" "}via {result.provider === "smtp" ? "company mailbox (SMTP)" : result.provider === "resend" ? "Resend" : "no provider"}
          {result.from ? ` · from ${result.from}` : ""}{result.error ? ` — ${result.error}` : ""}
          {result.status === "sent" && " · if it doesn't arrive in a minute, check Spam and press “Check delivery” below."}
        </div>
      )}
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider text-slate-500">Recent emails</div>
        <button className="text-xs text-blue-700 inline-flex items-center gap-1" onClick={load}><RefreshCw className="w-3 h-3" /> Refresh</button>
      </div>
      <div className="max-h-72 overflow-auto border border-slate-200 rounded-md divide-y divide-slate-100">
        {rows.length === 0 && <div className="text-xs text-slate-500 p-3">No emails yet.</div>}
        {rows.map((r) => (
          <div key={r.id} className="p-2 text-xs space-y-0.5">
            <div className="flex justify-between gap-2">
              <span className="truncate"><strong>{(r.to_emails || [r.to_email]).join(", ")}</strong> — {r.subject}</span>
              <span className={`shrink-0 ${tone(r.status)}`}>{r.status}{r.provider ? ` · ${r.provider}` : r.resend_id ? " · resend" : ""}</span>
            </div>
            <div className="text-slate-500 flex justify-between gap-2">
              <span>{r.created_at ? new Date(r.created_at).toLocaleString() : ""}{r.from_email ? ` · from ${r.from_email}` : ""}</span>
              {r.status === "sent" && (
                <button className="text-blue-700 shrink-0" onClick={() => check(r.id)} data-testid={`email-check-${r.id}`}>Check delivery</button>
              )}
            </div>
            {r.error && <div className="text-red-600">{r.error}</div>}
            {delivery[r.id] && (
              <div className={delivery[r.id].delivery === "delivered" || delivery[r.id].delivery === "handed_to_mailbox" ? "text-emerald-700" : "text-red-600"}>
                Delivery: {(delivery[r.id].delivery || "unknown").replace(/_/g, " ")}{delivery[r.id].detail ? ` — ${delivery[r.id].detail}` : ""}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AutoProcurementSendSettings() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/auto-procurement/settings").then((r) => setS(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Could not load Auto Procurement settings"));
  }, []);
  if (!s) return null;
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const payload = Object.fromEntries(FIELDS.map((k) => [k, s[k]]));
      const { data } = await api.put("/auto-procurement/settings", payload);
      setS((p) => ({ ...p, ...data }));
      toast.success("Auto Procurement settings saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };
  const waOk = s.whatsapp_provider === "webhook" ? s.whatsapp_webhook_configured : s.whatsapp_token_configured;
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6 max-w-3xl" data-testid="auto-procurement-settings-section">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-md bg-blue-50 flex items-center justify-center">
          <Send className="w-5 h-5 text-blue-700" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-slate-800" style={{ fontFamily: "Manrope" }}>Auto Procurement — Supplier PO sending</h3>
          <p className="text-xs text-slate-500">How Purchase Orders are sent to suppliers when you click Send (and by the automated MRP).</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-4 mb-4">
        <Status ok={s.email_provider_configured}
          okText={s.email_provider === "smtp" ? "Email: company mailbox (SMTP)" : "Email: Resend"}
          badText="No email provider on the server (SMTP_HOST or RESEND_API_KEY) — emails will only be queued" />
        <Status ok={waOk} okText="WhatsApp provider configured"
          badText={s.whatsapp_provider === "webhook" ? "WHATSAPP_WEBHOOK_URL not set on the server" : "WHATSAPP_ACCESS_TOKEN not set on the server"} />
      </div>
      {!waOk && (
        <p className="text-xs text-slate-500 -mt-2 mb-4">
          No WhatsApp API account? Turn off "Send POs on WhatsApp" and use the green "Share on WhatsApp" button on
          each PO — it opens that supplier's WhatsApp chat with the PO details and PDF link ready to send.
        </p>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <label className="flex items-center justify-between gap-3 border border-slate-200 rounded-md px-3 py-2">
          <span className="text-sm">Email POs to suppliers</span>
          <Switch checked={!!s.email_enabled} onCheckedChange={(v) => set("email_enabled", v)} data-testid="ap-send-email-enabled" />
        </label>
        <label className="flex items-center justify-between gap-3 border border-slate-200 rounded-md px-3 py-2">
          <span className="text-sm">Send POs on WhatsApp</span>
          <Switch checked={!!s.whatsapp_enabled} onCheckedChange={(v) => set("whatsapp_enabled", v)} data-testid="ap-send-whatsapp-enabled" />
        </label>
        <div>
          <Label>WhatsApp provider</Label>
          <select className="w-full h-9 border border-slate-200 rounded-md px-2 text-sm bg-white" value={s.whatsapp_provider}
            onChange={(e) => set("whatsapp_provider", e.target.value)} data-testid="ap-send-whatsapp-provider">
            <option value="meta_cloud">WhatsApp Business Cloud API</option>
            <option value="webhook">Gateway webhook</option>
          </select>
        </div>
        {s.whatsapp_provider !== "webhook" && (
          <div>
            <Label>WhatsApp phone number ID</Label>
            <Input value={s.whatsapp_phone_number_id || ""} onChange={(e) => set("whatsapp_phone_number_id", e.target.value)}
              placeholder="From Meta → WhatsApp → API Setup" data-testid="ap-send-whatsapp-phone-id" />
          </div>
        )}
        {s.whatsapp_provider !== "webhook" && (
          <>
            <div>
              <Label>Approved template name (optional)</Label>
              <Input value={s.whatsapp_template_name || ""} onChange={(e) => set("whatsapp_template_name", e.target.value)}
                placeholder="needed to message a supplier first" />
            </div>
            <div>
              <Label>Template language</Label>
              <Input value={s.whatsapp_template_lang || ""} onChange={(e) => set("whatsapp_template_lang", e.target.value)} placeholder="en" />
            </div>
          </>
        )}
        <div className="sm:col-span-2">
          <Label>CC on supplier PO emails (comma separated — add your own address to keep a copy)</Label>
          <Input value={(s.po_cc || []).join(", ")}
            onChange={(e) => set("po_cc", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))}
            data-testid="ap-send-po-cc" />
        </div>
      </div>
      <div className="flex items-center justify-between mt-4">
        <Link to="/auto-procurement?tab=settings" className="text-sm text-blue-700 hover:underline" data-testid="ap-send-more-link">
          All Auto Procurement settings (schedule, follow-ups, message templates) →
        </Link>
        <Button onClick={save} disabled={busy} data-testid="ap-send-save">{busy ? "Saving…" : "Save"}</Button>
      </div>
      <EmailDiagnostics />
    </div>
  );
}
